import * as Switch from '@radix-ui/react-switch';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import React, { useEffect, useState } from 'react';
import { useScreenSize } from '../../hooks/useScreenSize';
import { CTAButton } from '../buttons';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '../dialog/Dialog';
import { Divider } from '../divider/Divider';
import { ACCEPT_ALL_PREFERENCES, DEFAULT_PREFERENCES, conditionallyLoadAnalytics, DEFAULT_PREFERENCES_CONFIG, loadPreferences, savePreferences } from './preferences';
import { updateConsentMode } from '../../helpers/gtm/consent';
import { replayBufferedEvents } from '../../helpers/gtm/dataLayer';
import { group, log, groupEnd } from '../../helpers/gtm/logger';

import styles from './ConsentModal.module.scss';
import { Preferences, PreferencesCategory } from './types';
import { useTranslation } from 'react-i18next';

enum ConsentStep {
	Accept,
	Manage,
}

type ConsentStepProps = {
	saveConsent: (preferences: Preferences) => void;
	setOpen: (open: boolean) => void;
	setStep?: (step: ConsentStep) => void;
};

const AcceptConsentContent = ({ saveConsent, setOpen, setStep }: ConsentStepProps) => {
	const { t } = useTranslation();
	const handleAllowAllClick = () => {
		saveConsent(ACCEPT_ALL_PREFERENCES);
		setOpen(false);
	};

	const handleCustomizeClick = () => {
		setStep?.(ConsentStep.Manage);
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t('cookies.title')}</DialogTitle>
				<DialogDescription>
					{t('cookies.description')}
				</DialogDescription>
			</DialogHeader>

			<DialogFooter>
				<CTAButton buttonText={t('actions.allow_all')} onClick={handleAllowAllClick} />
				<CTAButton buttonText={t('actions.customize')} onClick={handleCustomizeClick} variant='text' textColor='#ffffff99' />
			</DialogFooter>
		</>
	);
};

const ManageConsentContent = ({ saveConsent, setOpen, initialPreferences }: ConsentStepProps & { initialPreferences: Preferences }) => {
	const { t } = useTranslation();
	const [preferences, setPreferences] = useState<Preferences>(initialPreferences);

	const handleAllowAllClick = () => {
		saveConsent(ACCEPT_ALL_PREFERENCES);
		setOpen(false);
	};

	const handleSaveSettingsClick = () => {
		saveConsent(preferences);
		setOpen(false);
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t('cookies.data_settings_title')}</DialogTitle>
				<DialogDescription>
					<VisuallyHidden.Root asChild>
						{t('cookies.data_settings_aria')}
					</VisuallyHidden.Root>
				</DialogDescription>
			</DialogHeader>

			<Divider />

			<DialogBody>
				<div className={styles.ConsentItems}>
					{Object.entries(DEFAULT_PREFERENCES_CONFIG).map(([category, p], i) => {
						return (
							<div className={styles.ConsentItem} key={`preference-${category}-${i}`}>
								<div className={styles.SwitchContainer}>
									<label htmlFor={category} className={styles.SwitchLabel}>{p.title}</label>
									<Switch.Root
										id={category}
										className={styles.SwitchRoot}
										checked={preferences?.[category] ?? true}
										disabled={category === PreferencesCategory.FUNCTIONAL}
										onCheckedChange={(checked) => setPreferences((prevPreferances) => {
											return {
												...prevPreferances,
												[category]: checked
											};
										})}
									>
										<Switch.Thumb className={styles.SwitchThumb} />
									</Switch.Root>
								</div>

								<p className={styles.ConsentDescription}>{p.description}</p>
							</div>
						);
					})}
				</div>
			</DialogBody>

			<DialogFooter>
				<CTAButton buttonText={t('actions.allow_all_cookies')} onClick={handleAllowAllClick} />

				<CTAButton buttonText={t('actions.allow_selection')} variant='secondary' onClick={handleSaveSettingsClick} />
			</DialogFooter>
		</>
	);
};

/**
 * Data-consent modal that gates both legacy analytics (Mixpanel/Statsig)
 * and Google Consent Mode v2 (analytics_storage, ad_storage, ad_user_data,
 * ad_personalization).
 *
 * Categories are driven by {@link DEFAULT_PREFERENCES_CONFIG} — adding a new
 * entry there automatically renders a new toggle row.
 *
 * **Consent update flow:**
 *  - On page load: {@link restoreSavedConsent} (called during bootstrapGTM,
 *    before GTM loads) pushes saved consent to the dataLayer. This is the
 *    single place returning-user consent is restored for GA/GTM.
 *  - On user action: {@link saveConsent} persists new preferences and pushes
 *    a live consent update via {@link updateConsentMode}.
 *  - Legacy analytics: {@link conditionallyLoadAnalytics} is called here on
 *    mount (for returning users) and on save (for new/changed preferences).
 *
 * **GDPR / Law 25 / ePrivacy**: Non-essential toggles default to OFF.
 * Pre-ticked boxes do not constitute valid consent even when an explicit
 * "Save" action is required (CJEU Planet49, Case C-673/17).
 */
const ConsentModal = () => {
	const [open, setOpen] = useState<boolean>(false);
	const [step, setStep] = useState<ConsentStep>(ConsentStep.Accept);
	const [savedPreferences, setSavedPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);

	const { isSmallScreen } = useScreenSize();

	// Load saved preferences on mount.
	// GA consent is already restored by restoreSavedConsent() in bootstrapGTM —
	// this useEffect only handles legacy analytics and modal visibility.
	useEffect(() => {
		const preferences = loadPreferences();
		if (!preferences) {
			setOpen(true);
		}
		else {
			setSavedPreferences(preferences);
			conditionallyLoadAnalytics({ preferences });
		}
	}, []);

	function saveConsent(preferences: Preferences) {
		group('[Consent] User consent submitted');
		log('Preferences:', preferences);

		savePreferences({ preferences });
		conditionallyLoadAnalytics({ preferences });
		updateConsentMode(preferences);
		if (preferences.analytics) {
			replayBufferedEvents();
		}

		groupEnd();
	}

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
			modal={isSmallScreen} // allow interaction of elements behind modal ONLY on desktop screen size
		>
			<DialogContent
				containerId='app'
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
			>
				<>
					{step === ConsentStep.Accept && (
						<AcceptConsentContent saveConsent={saveConsent} setOpen={setOpen} setStep={setStep} />
					)}

					{step === ConsentStep.Manage && (
						<ManageConsentContent saveConsent={saveConsent} setOpen={setOpen} initialPreferences={savedPreferences} />
					)}
				</>
			</DialogContent>
		</Dialog>
	);
};

export default ConsentModal;
